import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Utensils, ShieldCheck, FileText, Search, AlertTriangle, ChevronRight, Phone } from 'lucide-react';
export function FoodSafetyPage() {
  return <div className="min-h-screen flex flex-col w-full bg-gov-light" data-id="element-395">
      <Header data-id="element-396" />

      <main className="flex-grow" data-id="element-397">
        {/* Page Header */}
        <div className="bg-gov-navy text-white py-7 md:py-14" data-id="element-398">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-399">
            <nav className="flex text-sm text-gray-400 mb-5" aria-label="Breadcrumb" data-id="element-400">
              <ol className="flex items-center space-x-2" data-id="element-401">
                <li data-id="element-402">
                  <a href="#home" onClick={e => {
                  e.preventDefault();
                  window.location.hash = '';
                }} className="hover:text-white transition-colors" data-id="element-403">
                    Home
                  </a>
                </li>
                <li data-id="element-404">
                  <ChevronRight className="w-3.5 h-3.5" data-id="element-405" />
                </li>
                <li data-id="element-406">
                  <a href="#report" className="hover:text-white transition-colors" data-id="element-407">
                    Report a Concern
                  </a>
                </li>
                <li data-id="element-408">
                  <ChevronRight className="w-3.5 h-3.5" data-id="element-409" />
                </li>
                <li className="text-gov-amber font-medium" aria-current="page" data-id="element-410">
                  Food Safety
                </li>
              </ol>
            </nav>
            <div className="flex items-center space-x-3 mb-3" data-id="element-411">
              <div className="bg-gov-amber/20 p-2 rounded-lg" data-id="element-412">
                <Utensils className="w-5 h-5 text-gov-amber" data-id="element-413" />
              </div>
              <span className="text-gov-amber font-semibold tracking-wider uppercase text-xs" data-id="element-414">
                Environmental Health Division
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mb-3" data-id="element-415">
              Report a Foodborne Illness
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl leading-relaxed" data-id="element-416">
              Help us identify outbreaks and protect Franklin County residents
              by reporting suspected food-related illnesses.
            </p>
          </div>
        </div>

        {/* ===== PRIMARY SECTION: Form + Context ===== */}
        <section className="bg-white border-b border-gov-border" data-id="element-417">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-16" data-id="element-418">
            {/* Mobile: form appears first via order classes */}
            <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-16" data-id="element-419">
              {/* Left Column: Context & encouragement */}
              <div className="lg:w-1/2 order-2 lg:order-1" data-id="element-420">
                <div className="lg:sticky lg:top-32 space-y-8" data-id="element-421">
                  <div data-id="element-422">
                    <h2 className="text-2xl md:text-3xl font-bold text-gov-navy mb-5" data-id="element-423">
                      Suspected food poisoning?
                      <br data-id="element-424" />
                      We need to hear from you.
                    </h2>
                    <p className="text-gov-slate text-lg leading-relaxed mb-4" data-id="element-425">
                      If you or someone in your household became ill after
                      eating at a restaurant, grocery store, food truck, or any
                      food establishment in Franklin County, please fill out the
                      report form.
                    </p>
                    <p className="text-gov-slate leading-relaxed mb-6" data-id="element-426">
                      Every report matters. Even if you're unsure, your
                      information helps our epidemiologists detect patterns,
                      launch investigations, and prevent further illness in the
                      community.
                    </p>
                  </div>

                  {/* What happens next */}
                  <div className="bg-gov-light rounded-xl p-6 border border-gov-border" data-id="element-427">
                    <h3 className="font-bold text-gov-navy mb-3" data-id="element-428">
                      What happens after you report?
                    </h3>
                    <ol className="space-y-3 text-sm text-gov-slate" data-id="element-429">
                      <li className="flex items-start" data-id="element-430">
                        <span className="bg-gov-navy text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-431">
                          1
                        </span>
                        <span data-id="element-432">
                          Your report is reviewed by our food safety team within{' '}
                          <strong className="text-gov-navy" data-id="element-433">
                            one business day
                          </strong>
                          .
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-434">
                        <span className="bg-gov-navy text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-435">
                          2
                        </span>
                        <span data-id="element-436">
                          If a pattern is detected, an environmental health
                          specialist investigates the establishment.
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-437">
                        <span className="bg-gov-navy text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-438">
                          3
                        </span>
                        <span data-id="element-439">
                          You may be contacted for follow-up details to support
                          the investigation.
                        </span>
                      </li>
                    </ol>
                  </div>

                  {/* Urgent callout */}
                  <div className="flex items-start space-x-3 bg-amber-50 border border-amber-200 rounded-xl p-5" data-id="element-440">
                    <Phone className="w-5 h-5 text-gov-amber flex-shrink-0 mt-0.5" data-id="element-441" />
                    <div data-id="element-442">
                      <p className="text-sm text-gov-slate" data-id="element-443">
                        <strong className="text-gov-navy" data-id="element-444">
                          Need immediate help?
                        </strong>{' '}
                        For urgent food safety concerns, call us directly at{' '}
                        <strong className="text-gov-navy" data-id="element-445">
                          (555) 123-4568
                        </strong>{' '}
                        during business hours.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: The Form — appears first on mobile */}
              <div className="lg:w-1/2 order-1 lg:order-2" data-id="element-446">
                <div className="flex flex-col items-center" data-id="element-447">
                  <div className="flex items-center space-x-2 mb-5" data-id="element-448">
                    <AlertTriangle className="w-5 h-5 text-gov-amber" data-id="element-449" />
                    <h3 className="font-bold text-gov-navy text-lg" data-id="element-450">
                      Submit Your Report
                    </h3>
                  </div>

                  <div className="flex justify-center w-full" data-id="element-451">
                    <iframe src="https://iwaspoisoned.com/widget/gov/46?=dinesafe-test1&form=1" height="550" width="100%" allow="microphone" style={{
                    outline: 'none',
                    border: 'none',
                    maxWidth: '450px'
                  }} title="Foodborne Illness Reporting Form" data-id="element-452"></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== SECONDARY CONTENT: Program Info, Inspections, Resources ===== */}
        <div style={{
        backgroundColor: '#f5f5f5'
      }} data-id="element-453">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 space-y-16" data-id="element-454">
            {/* Program Overview */}
            <section data-id="element-455">
              <h2 className="text-2xl font-bold text-gov-navy mb-6" data-id="element-456">
                About Our Food Safety Program
              </h2>
              <p className="text-gov-slate leading-relaxed mb-8 max-w-3xl" data-id="element-457">
                The Franklin County Food Safety Program works to prevent
                foodborne illness by ensuring that food provided for human
                consumption is safe, unadulterated, and honestly presented. Our
                registered environmental health specialists conduct unannounced
                inspections of all licensed food facilities.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-458">
                <div className="bg-white p-6 rounded-xl border border-gov-border shadow-sm" data-id="element-459">
                  <ShieldCheck className="w-7 h-7 text-gov-navy mb-3" data-id="element-460" />
                  <h3 className="text-base font-bold text-gov-navy mb-2" data-id="element-461">
                    Restaurant Inspections
                  </h3>
                  <p className="text-sm text-gov-slate" data-id="element-462">
                    Routine, unannounced inspections of over 3,000 food
                    establishments annually.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl border border-gov-border shadow-sm" data-id="element-463">
                  <FileText className="w-7 h-7 text-gov-navy mb-3" data-id="element-464" />
                  <h3 className="text-base font-bold text-gov-navy mb-2" data-id="element-465">
                    Licensing & Permits
                  </h3>
                  <p className="text-sm text-gov-slate" data-id="element-466">
                    All food service operations must obtain a valid license
                    before operating in the county.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gov-border shadow-sm" data-id="element-467">
                  <Utensils className="w-7 h-7 text-gov-navy mb-3" data-id="element-468" />
                  <h3 className="text-base font-bold text-gov-navy mb-2" data-id="element-469">
                    Food Handler Training
                  </h3>
                  <p className="text-sm text-gov-slate" data-id="element-470">
                    Required certification programs for food service workers to
                    ensure safe handling practices.
                  </p>
                </div>
              </div>
            </section>

            {/* Inspection Scores + Resources side by side */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-471">
              {/* Inspection Scores */}
              <div className="bg-gov-navyDark text-white rounded-xl p-8 shadow-md flex flex-col" data-id="element-472">
                <Search className="w-8 h-8 text-gov-amber mb-4" data-id="element-473" />
                <h3 className="text-xl font-bold mb-3" data-id="element-474">
                  Check Inspection Scores
                </h3>
                <p className="text-gray-300 text-sm mb-6 flex-grow" data-id="element-475">
                  Before you dine out, look up the latest health inspection
                  results for restaurants and food facilities in Franklin
                  County.
                </p>
                <a href="#" className="block w-full text-center bg-gov-amber hover:bg-gov-amberHover text-gov-navyDark font-bold py-3 px-4 rounded transition-colors" data-id="element-476">
                  Search Restaurant Scores
                </a>
              </div>

              {/* Resources */}
              <div className="bg-white rounded-xl border border-gov-border p-8 shadow-sm" data-id="element-477">
                <h3 className="text-xl font-bold text-gov-navy mb-5" data-id="element-478">
                  Food Safety Resources
                </h3>

                <ul className="space-y-3" data-id="element-479">
                  <li data-id="element-480">
                    <a href="#" className="flex items-center text-gov-slate hover:text-gov-navy hover:underline transition-all text-sm" data-id="element-481">
                      <ChevronRight className="w-4 h-4 mr-2 text-gov-amber flex-shrink-0" data-id="element-482" />
                      Food Handler Training & Certification
                    </a>
                  </li>
                  <li data-id="element-483">
                    <a href="#" className="flex items-center text-gov-slate hover:text-gov-navy hover:underline transition-all text-sm" data-id="element-484">
                      <ChevronRight className="w-4 h-4 mr-2 text-gov-amber flex-shrink-0" data-id="element-485" />
                      Apply for a Food Service License
                    </a>
                  </li>
                  <li data-id="element-486">
                    <a href="#" className="flex items-center text-gov-slate hover:text-gov-navy hover:underline transition-all text-sm" data-id="element-487">
                      <ChevronRight className="w-4 h-4 mr-2 text-gov-amber flex-shrink-0" data-id="element-488" />
                      Mobile Food Truck Requirements
                    </a>
                  </li>
                  <li data-id="element-489">
                    <a href="#" className="flex items-center text-gov-slate hover:text-gov-navy hover:underline transition-all text-sm" data-id="element-490">
                      <ChevronRight className="w-4 h-4 mr-2 text-gov-amber flex-shrink-0" data-id="element-491" />
                      Temporary Event Food Permits
                    </a>
                  </li>

                  <li data-id="element-492">
                    <a href="#" className="flex items-center text-gov-slate hover:text-gov-navy hover:underline transition-all text-sm" data-id="element-493">
                      <ChevronRight className="w-4 h-4 mr-2 text-gov-amber flex-shrink-0" data-id="element-494" />
                      Current Food Recalls & Alerts
                    </a>
                  </li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer data-id="element-495" />
    </div>;
}